import React, { useEffect, useState } from 'react';
import { Mail, Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import api from '../../utils/api';
import { authErrorMessage } from '../../utils/authNavigation';

export const EmailLinkRequest = ({ destination, email, setEmail, passwordMode, children }) => {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retryAt, setRetryAt] = useState(0);
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    const update = () => setRemaining(Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [retryAt]);

  const requestLink = async (event) => {
    event.preventDefault();
    if (busy || remaining) return;
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/auth/magic-link/request', { email: email.trim(), destination });
      setSent(true);
      setRetryAt(Date.now() + data.retry_after * 1000);
    } catch (err) {
      setError(authErrorMessage(err, 'Your sign-in email could not be sent. Please try again.'));
      const delay = Number(err.response?.data?.detail?.retry_after || err.response?.headers?.['retry-after']);
      if (delay > 0) setRetryAt(Date.now() + delay * 1000);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="email-link-request">
      {sent && !passwordMode ? (
        <section className="mb-6 border-l-2 border-indigo-400 pl-4" aria-live="polite" data-testid="magic-check-email-state">
          <h2 className="text-lg font-semibold text-white mb-2">Check your email</h2>
          <p className="text-sm text-slate-300 break-words" data-testid="magic-sent-address">{email}</p>
          <p className="text-sm text-slate-400 mt-3">If this address is eligible, your link will arrive shortly. Check spam or junk, too. Only the newest link works, for 15 minutes.</p>
          <Button type="button" variant="ghost" className="mt-3 -ml-3 text-indigo-300" onClick={() => { setSent(false); setError(''); }} data-testid="magic-correct-email-button"><ArrowLeft className="w-4 h-4" />Use a different email</Button>
        </section>
      ) : (
        <div className="mb-5">
          <Label htmlFor="login-email" className="text-slate-300" data-testid="login-email-label">Email address</Label>
          <Input id="login-email" type="email" autoComplete="email" autoCapitalize="none" spellCheck={false} required maxLength={254} value={email} onChange={event => { setEmail(event.target.value); setError(''); setRetryAt(0); }} className="mt-2 h-12 bg-slate-950 border-slate-700 text-white" placeholder="you@company.com" data-testid="login-email-input" form={passwordMode ? 'password-login-form' : 'magic-request-form'} />
        </div>
      )}
      {!passwordMode && <form id="magic-request-form" onSubmit={requestLink} data-testid="magic-request-form">
        {error && <p role="alert" className="text-sm text-rose-300 mb-4" data-testid="magic-request-error">{error}</p>}
        <Button type="submit" disabled={busy || remaining > 0} className="h-auto min-h-12 w-full whitespace-normal py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full" data-testid="magic-request-submit-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : sent ? <RefreshCw className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
          <span data-testid="magic-request-button-label">{busy ? 'Sending…' : remaining > 0 ? `Request another link in ${remaining}s` : sent ? 'Email me another link' : 'Email me a sign-in link'}</span>
        </Button>
        {!sent && <p className="text-xs text-slate-400 mt-3" data-testid="magic-request-new-account-note">New here? You can create a participant account after verifying your email.</p>}
      </form>}
      {children}
    </div>
  );
};